import { AuthErrorProps, AuthSuccessProps, OAuthErrorInfo, User } from '@/auth/types';
import { ConnectionInfo, UpdateProfileProps, SignupProps } from '@/methods/types';
import type { RateLimitType } from '@/rate-limit/types';

/**
 * A single rate-limit rule for an authentication bucket. The `bucket` is
 * implied by which auth action you're configuring (e.g. `signup`), so callers
 * only specify the actor type, window size, and limit.
 *
 * @example
 * ```typescript
 * import { time } from 'modelence';
 *
 * const rule: AuthRateLimitOverride = {
 *   type: 'ip',
 *   window: time.minutes(15),
 *   limit: 10,
 * };
 * ```
 */
export type AuthRateLimitOverride = {
  /** Identifier type of the actor this rule applies to. */
  type: RateLimitType;
  /** Time window size in milliseconds. Use `time.minutes(15)` etc. */
  window: number;
  /** Maximum allowed hits within the window. */
  limit: number;
};

/**
 * Per-action rate limit overrides for authentication endpoints.
 *
 * Each bucket accepts an array of rules that are merged into the built-in
 * defaults by `(type, window)` tuple:
 *   - A rule whose `(type, window)` matches a default replaces that default's
 *     `limit`.
 *   - A rule whose `(type, window)` does not match any default is added as
 *     an extra rule for the bucket.
 *   - Defaults whose `(type, window)` is not overridden are kept.
 *
 * This means you can tighten a single window without accidentally dropping
 * the other built-in protections for that bucket.
 *
 * @example Tighten the 15-minute signup cap; per-day default is preserved.
 * ```typescript
 * import { startApp } from 'modelence/server';
 * import { time } from 'modelence';
 *
 * startApp({
 *   auth: {
 *     rateLimits: {
 *       signup: [
 *         { type: 'ip', window: time.minutes(15), limit: 5 },
 *       ],
 *     },
 *   },
 * });
 * ```
 *
 * @example Add an extra window alongside the defaults.
 * ```typescript
 * startApp({
 *   auth: {
 *     rateLimits: {
 *       signup: [
 *         { type: 'ip', window: time.minutes(1), limit: 2 },
 *       ],
 *     },
 *   },
 * });
 * ```
 */
export type AuthRateLimitsConfig = {
  /** Per-IP limits for the signup endpoint (successful signups only). */
  signup?: AuthRateLimitOverride[];
  /** Per-IP limits for signup attempts (checked before duplicate detection). */
  signupAttempt?: AuthRateLimitOverride[];
  /** Per-IP limits for login attempts. */
  signin?: AuthRateLimitOverride[];
  /** Per-user and per-IP limits for email verification resend requests. */
  verification?: AuthRateLimitOverride[];
  /** Rate limits for password reset requests. */
  passwordReset?: AuthRateLimitOverride[];
  /** Rate limits for magic link requests. */
  magicLink?: AuthRateLimitOverride[];
  /** Rate limits for one-time code sign-in attempts. */
  oneTimeCode?: AuthRateLimitOverride[];
  /** Rate limits for redeeming a mobile OAuth exchange code. */
  oauthExchange?: AuthRateLimitOverride[];
  /** Per-user rate limits for profile updates. */
  updateProfile?: AuthRateLimitOverride[];
};

type GenerateHandleProps = {
  email: string;
  firstName?: string;
  lastName?: string;
};

/**
 * The password-setting flow that invoked {@link AuthConfig.validatePassword}.
 *
 * Every path that writes a password hash passes one of these, so a policy can
 * be uniform by default while still special-casing a flow if it needs to.
 */
export type PasswordContext = 'signup' | 'reset';

/**
 * Props passed to {@link AuthConfig.validatePassword}.
 */
export type ValidatePasswordProps = {
  /** The candidate plaintext password, after the built-in length checks. */
  password: string;
  /**
   * Lowercased email address of the account the password is being set on.
   * Useful for rejecting passwords that contain the local-part.
   */
  email: string;
  /** Which flow is setting the password. */
  context: PasswordContext;
};

/**
 * Props passed to {@link AuthConfig.onBeforeSignup}.
 *
 * The hook fires after validation and the built-in disposable-email check,
 * but before the user document is inserted. Throwing aborts the signup —
 * the thrown error is re-thrown to the caller and `onSignupError` fires.
 */
export type BeforeSignupProps = {
  /** Lowercased, validated email address. */
  email: string;
  firstName?: string;
  lastName?: string;
  handle?: string;
  /** Provider that initiated the signup: `'email'` or `'magicLink'`. */
  provider: 'email' | 'magicLink';
  connectionInfo?: ConnectionInfo;
};

/**
 * Callback options for authentication operations
 */
export type AuthOption = {
  /** Callback executed when authentication succeeds */
  onSuccess?: (user: User) => void;
  /** Callback executed when authentication fails */
  onError?: (error: Error) => void;
};

/**
 * Authentication configuration for the application
 *
 * @example
 * ```typescript
 * import { startApp } from 'modelence/server';
 * import { time } from 'modelence';
 *
 * startApp({
 *   auth: {
 *     validateSignup: ({ email, firstName, lastName, password, handle, avatarUrl }) => {
 *       // Validating the signup data
 *       if (!email || !password) {
 *         throw new Error('Email and password are required');
 *       }
 *     },
 *     onAfterLogin: ({ user }) => {
 *       console.log('User logged in:', user.name);
 *       // Redirect to dashboard
 *     },
 *     onLoginError: ({ error }) => {
 *       console.error('Login failed:', error.message);
 *       // Show error toast
 *     },
 *     onAfterSignup: ({ user }) => {
 *       console.log('User signed up:', user.email);
 *       // Send welcome email
 *     },
 *     onSignupError: ({ error }) => {
 *       console.error('Signup failed:', error.message);
 *     },
 *     generateHandle: ({ email }) => {
 *       console.log('Generating handle for:', email);
 *       // Generate handle
 *       return 'user123';
 *     },
 *   }
 * });
 * ```
 */
export type AuthConfig = {
  /**
   * Pre-signup validation hook. Runs before a new user is created during
   * email/password signup, after format checks but before duplicate detection.
   * Throw to reject the signup — the thrown message is surfaced to the client.
   *
   * Receives the raw signup payload (`email`, `password`, and optional
   * `firstName`, `lastName`, `avatarUrl`, `handle`). May be async.
   */
  validateSignup?: (props: SignupProps) => void | Promise<void>;

  /**
   * Password policy hook. Runs on **every** path that sets a password — signup
   * and password reset today, plus any future change-password flow — after the
   * built-in length checks (`MIN_PASSWORD_LENGTH`..`MAX_PASSWORD_LENGTH`) and
   * before the password is hashed. Throw to reject the password; the thrown
   * message is surfaced to the client.
   *
   * Prefer this over {@link AuthConfig.validateSignup} for password rules.
   * `validateSignup` only runs at signup, so a policy enforced there alone can
   * be bypassed by signing up with a compliant password and immediately
   * resetting to a weaker one.
   *
   * Receives `{ password, email, context }`, where `context` is the flow that
   * triggered the check (`'signup'` or `'reset'`). May be async.
   *
   * @example Enforce a 12-character minimum everywhere.
   * ```typescript
   * startApp({
   *   auth: {
   *     validatePassword: ({ password }) => {
   *       if (password.length < 12) {
   *         throw new Error('Password must be at least 12 characters');
   *       }
   *     },
   *   },
   * });
   * ```
   */
  validatePassword?: (props: ValidatePasswordProps) => void | Promise<void>;

  /**
   * Pre-update validation hook. Runs before a user's profile fields
   * (`firstName`, `lastName`, `avatarUrl`, `handle`) are written.
   * Throw to reject the update — the thrown message is surfaced to the client.
   * May be async.
   */
  validateProfileUpdate?: (props: UpdateProfileProps) => void | Promise<void>;

  /**
   * Fires after a successful login (email/password or OAuth) once the session
   * has been linked to the user. Receives `{ provider, user, session, connectionInfo }`.
   * Use for analytics, audit logging, or post-login side effects.
   */
  onAfterLogin?: (props: AuthSuccessProps) => void;

  /**
   * Fires when a login attempt fails. Receives `{ provider, error, session, connectionInfo }`.
   * Use for failure analytics or alerting — does NOT change the response sent to the client.
   */
  onLoginError?: (props: AuthErrorProps) => void;

  /**
   * Hook fired after validation and the built-in disposable-email check, but
   * before the new user document is inserted. Throwing aborts the signup —
   * the thrown error is re-thrown to the caller and `onSignupError` fires.
   *
   * Use this to plug in a custom domain-policy check (e.g. a tenant-specific
   * email-domain verification service) without having to disable the built-in
   * disposable-email check.
   *
   * Invoked for `'email'` and `'magicLink'` provider signups. OAuth signups
   * are not gated because OAuth providers (Google, GitHub, etc.) do not issue
   * disposable accounts.
   */
  onBeforeSignup?: (props: BeforeSignupProps) => void | Promise<void>;

  /**
   * Fires after a successful signup once the user record is created and the
   * session is linked. Receives `{ provider, user, session, connectionInfo }`.
   * Common uses: send welcome email, create default workspace, track activation.
   */
  onAfterSignup?: (props: AuthSuccessProps) => void;

  /**
   * Fires when a signup attempt fails (validation, duplicate email, etc.).
   * Receives `{ provider, error, session, connectionInfo }`.
   * Use for failure analytics — does NOT change the response sent to the client.
   */
  onSignupError?: (props: AuthErrorProps) => void;

  /**
   * Fires after a user's email is successfully verified (via the verification
   * link or implicitly via password reset). Receives `{ provider, user, session, connectionInfo }`.
   */
  onAfterEmailVerification?: (props: AuthSuccessProps) => void;

  /**
   * Fires when email verification fails (invalid or expired token).
   * Receives `{ provider, error, session, connectionInfo }`.
   */
  onEmailVerificationError?: (props: AuthErrorProps) => void;

  /**
   * Fires after an OAuth provider is linked to an existing account
   * (either automatically when `oauthAccountLinking: 'auto'` or via an
   * explicit link flow). Receives `{ provider, user, session, connectionInfo }`.
   */
  onAfterOAuthLink?: (props: AuthSuccessProps) => void;

  /**
   * Fires when OAuth account linking fails. Receives
   * `{ provider, error, session, connectionInfo }`.
   */
  onOAuthLinkError?: (props: AuthErrorProps) => void;

  /**
   * Custom handle generator. If provided, overrides the default behavior
   * (which derives the handle from the email local-part). Receives
   * `{ email, firstName?, lastName? }` and returns the desired handle
   * synchronously or as a `Promise<string>`. The returned handle must contain
   * only letters, numbers, underscores, and hyphens (matching `HANDLE_REGEX`).
   * If the returned handle collides with an existing one, Modelence appends
   * a numeric suffix automatically.
   */
  generateHandle?: (props: GenerateHandleProps) => Promise<string> | string;

  /** @deprecated Use {@link AuthConfig.onAfterLogin} and {@link AuthConfig.onLoginError} instead. */
  login?: AuthOption;
  /** @deprecated Use {@link AuthConfig.onAfterSignup} and {@link AuthConfig.onSignupError} instead. */
  signup?: AuthOption;

  /**
   * Enables passwordless magic link authentication. Disabled by default.
   *
   * Requires an email provider and delivery settings under the `email` option
   * (see `EmailConfig.magicLink`).
   *
   * @example
   * ```typescript
   * startApp({
   *   auth: {
   *     magicLink: { enabled: true, allowSignup: true },
   *   },
   * });
   * ```
   */
  magicLink?: {
    enabled?: boolean;
    /**
     * Allows a magic link (or its one-time code) to create an account when
     * the email has no existing one — combined sign-in/sign-up, like OAuth.
     * Disabled by default: unknown emails get the same generic "link sent"
     * response but no email, and no account is ever auto-created.
     */
    allowSignup?: boolean;
  };

  /**
   * Controls how OAuth providers handle existing accounts with matching email.
   * - 'manual' (default): Returns an error when an OAuth login matches an existing email.
   * - 'auto': Automatically links the OAuth provider to the existing account
   *   if the provider email is verified.
   */
  oauthAccountLinking?: 'auto' | 'manual';

  /**
   * Customizes how OAuth authentication errors are rendered. By default,
   * OAuth errors are returned as JSON; providing this returns a custom HTML
   * response instead, which is useful when the OAuth flow runs in a browser
   * context. Receives `{ error, statusCode }` and returns an HTML string
   * (or `null`/`undefined` to fall back to the default JSON response).
   *
   * Always escape interpolated values to prevent XSS.
   *
   * @deprecated Use {@link AuthConfig.oauthErrorRedirectUrl} instead. It sends
   * the browser back into the app, where the error can be shown on a real page
   * with the app's own UI; this only ever renders a standalone document at the
   * callback URL. Ignored when `oauthErrorRedirectUrl` is set.
   */
  errorComponent?: (props: OAuthErrorInfo) => string | null | undefined;

  /**
   * Where a failed web OAuth flow sends the browser. A path (`'/login'`),
   * resolved against `_system.site.url`, or an absolute URL. The failure is
   * appended as `?error=<message>&errorCode=<code>`,
   * the same contract a mobile flow delivers on its deep link, so the app can
   * show the message on a real page instead of the raw callback response.
   *
   * OAuth errors surface at the provider callback URL, outside any client
   * bundle, so nothing there can render app UI. Without this (or
   * {@link AuthConfig.errorComponent}) the user lands on a JSON body with no
   * way back. When both are set, the redirect wins and `errorComponent` is not
   * called.
   *
   * `error` is for display and its wording may change; branch on `errorCode`.
   *
   * Applies to the provider callback only. A request rejected at the sign-in
   * *initiation* endpoint (auth not configured, a disallowed mobile
   * `redirectUri`, a missing `codeChallenge`) still answers the calling client
   * with JSON, since nothing has left for the provider yet.
   *
   * @example
   * ```typescript
   * startApp({
   *   auth: {
   *     oauthErrorRedirectUrl: '/login',
   *   },
   * });
   * ```
   */
  oauthErrorRedirectUrl?: string;

  /**
   * Overrides the built-in rate limits for authentication endpoints. Each rule
   * you provide is merged into the defaults by `(bucket, type, window)`:
   * matching tuples replace the default `limit`, new tuples are added, and
   * unspecified defaults are preserved. See {@link AuthRateLimitsConfig} for
   * full semantics and examples.
   */
  rateLimits?: AuthRateLimitsConfig;

  /**
   * When `true`, the built-in disposable-email check is skipped during signup.
   * Defaults to `false` (built-in check enforced).
   *
   * Set this to `true` when you want to enforce your own domain-policy logic
   * via {@link onBeforeSignup} — for example, a service that classifies
   * domains as public/disposable/custom with its own data sources and cache.
   *
   * Skipping the built-in check without registering an `onBeforeSignup` hook
   * means disposable emails will be allowed to sign up.
   */
  allowDisposableEmails?: boolean;

  /**
   * Settings for authenticating from a native (React Native / Expo) client.
   *
   * @example
   * ```typescript
   * startApp({
   *   auth: {
   *     mobile: { redirectUrls: ['myapp://auth'] },
   *   },
   * });
   * ```
   */
  mobile?: {
    /**
     * Deep links the OAuth callback is allowed to redirect a native app back to,
     * e.g. `['myapp://auth']`. A sign-in request naming any other target is
     * rejected before the user ever reaches the provider's consent screen.
     *
     * This is an allowlist because the redirect target decides where an auth
     * flow ends up: without it, a crafted link could point the callback at an
     * attacker-controlled destination. There is no implicit default — mobile
     * OAuth stays disabled until at least one entry is configured here or via
     * the `auth.mobile.redirectUrls` config value (the two are merged).
     *
     * Entries are matched on scheme, host and path; a request may add query
     * parameters but may not change any of those three.
     */
    redirectUrls?: string[];
  };
};

let authConfig: AuthConfig = Object.freeze({});

export function setAuthConfig(newAuthConfig: AuthConfig) {
  authConfig = Object.freeze(Object.assign({}, authConfig, newAuthConfig));
}

export function getAuthConfig() {
  return authConfig;
}
